import { useState } from 'react'
import { styled, SxProps, useTheme } from '@mui/material'
import { useInView } from 'react-intersection-observer'
import {
  useWappStage,
  WapprunnerCreate as WappRunnerCreate,
} from 'weblite-web-wapprunner'
import { WappMode } from '@weblite-wapps/protocol'
import { RawWappMessage, WappMessage } from '@weblite/ts-action/message'
import { NonInlineWapp } from './non-inline-wapp'
import { MESSAGE_BG_BOX_SHADOW } from '$setup/style/constants'
import { useSendStatus } from '../../components/time-and-status/send-status.react-hooks'
import { PendingWappLoading } from './pending-wapp-loading'

const MAX_WAPP_HEIGHT = 400
const MIN_FEASIBLE_SIZE = 100

const Container = styled('div')<{ isLoaded: boolean; fromMe: boolean }>`
  display: flex;
  flex-direction: column;

  position: relative;
  align-items: center;
  overflow: hidden;
  width: calc(100% - 20px);
  height: 100%;

  box-sizing: unset;
  background-color: ${({ isLoaded, theme }) =>
    isLoaded ? 'transparent' : theme.palette.neutral000};
  border-radius: 12px;
  box-shadow: ${MESSAGE_BG_BOX_SHADOW};
  margin-right: ${({ fromMe }) => (fromMe ? '0' : 'auto')};
  margin-left: ${({ fromMe }) => (fromMe ? 'auto' : '0')};
`

export function Wapp({
  attachment,
  statusComp,
  rootStyle,
  messageObj,
  fromMe,
  isChannel,
}: {
  attachment: RawWappMessage['attachment']
  statusComp: JSX.Element | null
  rootStyle?: SxProps
  messageObj: WappMessage
  fromMe: boolean
  isChannel: boolean
}) {
  const { iid, roomId } = messageObj
  // @ts-ignore // TODO: check why wapp message attachment is optional
  const { wappId, wisId, width, height } = attachment

  const [isObserved, setIsObserved] = useState(false)
  const { ref: containerRef } = useInView({
    skip: isObserved,
    onChange: inView => {
      if (inView) {
        setIsObserved(true)
      }
    },
  })

  const linearGradiant = useTheme().palette.lightChatSenderGradient
  const [wappSessionId, setWappSessionId] = useState('')
  const wappStage = useWappStage(wappSessionId)

  const clampedWidth = width ? Math.min(280, width) : 280
  const clampedHeight = height
    ? Math.min(MAX_WAPP_HEIGHT, height * (clampedWidth / width!))
    : MAX_WAPP_HEIGHT

  const hasInlineWapp = Boolean(width && height)

  const status = useSendStatus(iid, roomId)

  const isPending = status === 'PENDING'

  /**
   * How to use HeightSlope. (not used so far)
   *
   * === NO heightSlope ===
   * `width` and `height` are necessary
   *
   * height(CONST_HEIGHT)
   * constant used to edit dynamic generated height can be also negative,
   * it will be clamped by (MAX_FEASIBLE_HEIGHT=600px).
   *
   * width(CONST_WIDTH)
   * maximum define width when no heightSlope is set
   * it will be clamped by (MAX_FEASIBLE_WIDTH=min(78% of chat_size, 320px)).
   *
   * === WITH heightSlope ===
   * `width` is optionals and initiated with 0
   *
   * missing width (width=0) cause a wapp to gain maximum possible width
   * that a message can have (MAX_FEASIBLE_WIDTH).
   *
   * height can be set to fixed value or use in responsive manner via width calculated ratio.
   * minimum height for wapp is 10 pixel so lower height are ignored internally
   */
  // () => ({
  //   width: width ? `${width}px` : '100%',
  //   height:
  //     heightSlope != null
  //       ? `calc(100% * ${heightSlope} + (${height}px))`
  //       : width
  //       ? `calc(100% * ${height / width})`
  //       : `${height}px`,
  // }),
  // [width, height, heightSlope],
  // )

  return (
    <>
      {hasInlineWapp ? (
        <Container
          ref={containerRef}
          sx={{
            ...rootStyle,
            border: isPending ? 'none' : '1px solid #E6E6E6',
            minWidth: `${MIN_FEASIBLE_SIZE}px`,
            minHeight: `${MIN_FEASIBLE_SIZE}px`,
            width: `${clampedWidth}px`,
            height: `${clampedHeight}px`,
            maxHeight: `${MAX_WAPP_HEIGHT}px`,
          }}
          isLoaded={wappStage === 'running'}
          fromMe={fromMe}
        >
          {isObserved &&
            (isPending ? (
              <PendingWappLoading />
            ) : (
              <WappRunnerCreate
                key={wappId}
                wappId={wappId}
                wisId={wisId}
                mode={WappMode.INLINE}
                updateWappSessionId={setWappSessionId}
              />
            ))}
        </Container>
      ) : (
        <NonInlineWapp
          wappId={wappId}
          wisId={wisId}
          fromMe={!isChannel && fromMe}
        />
      )}

      {statusComp ? (
        <div
          style={{
            padding: '7px 10px',
            background: !isChannel && fromMe ? linearGradiant : 'white',
            marginTop: '6px',
            width: 'fit-content',
            borderRadius: '12px',
            marginRight: fromMe ? '0' : 'auto',
            marginLeft: fromMe ? 'auto' : '0',
            boxShadow: MESSAGE_BG_BOX_SHADOW,
          }}
        >
          {statusComp}
        </div>
      ) : null}
    </>
  )
}

Wapp.defaultProps = {
  rootStyle: null,
}
